import type { IncidentListResponse, IncidentListSortKey } from '@prerender/diagnostics-api';
import { Table } from 'react-bootstrap';

import IncidentColumnFilter, { type IncidentColumnFilterKey } from './incidentColumnFilters';
import IncidentColumnHeader from './IncidentColumnHeader';
import type { IncidentListParams } from './incidentListParams';
import { INCIDENT_LIST_PAGE_SIZE } from './incidentListQueryArgs';
import IncidentRow from './IncidentRow';
import IncidentsPager from './IncidentsPager';
import type { IncidentListFilterPatch } from './useIncidentListParams';

type Column = { label: string; sortKey?: IncidentListSortKey; filterKey?: IncidentColumnFilterKey };

const COLUMNS: Column[] = [
  { label: 'Incident ID' },
  { label: 'Account', sortKey: 'accountName' },
  { label: 'Plan', sortKey: 'plan', filterKey: 'planTier' },
  { label: 'Severity', sortKey: 'severity', filterKey: 'severity' },
  { label: 'Status', sortKey: 'status', filterKey: 'status' },
  { label: 'Detected', sortKey: 'firstDetectedAt' },
  { label: 'Last checked', sortKey: 'lastCheckedAt' },
  { label: 'Type', sortKey: 'incidentType', filterKey: 'incidentType' },
  { label: 'Incident' },
  { label: 'Impact' },
];

const REVIEW_COLUMN = 'Review';

const COLUMN_COUNT = COLUMNS.length + 1;

type IncidentsTableProps = {
  incidents: IncidentListResponse;
  refreshing: boolean;
  params: IncidentListParams;
  now: Date;
  onFiltersChange: (patch: IncidentListFilterPatch) => void;
  onSort: (key: IncidentListSortKey) => void;
  onPageChange: (page: number) => void;
};

function IncidentsTable({
  incidents: { items, totalCount },
  refreshing,
  params,
  now,
  onFiltersChange,
  onSort,
  onPageChange,
}: IncidentsTableProps): JSX.Element {
  return (
    <div
      className={refreshing ? 'diagnostics-results diagnostics-results-refreshing' : 'diagnostics-results'}
      aria-busy={refreshing}
    >
      <div className="table-responsive">
        <Table hover className="diagnostics-table">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <IncidentColumnHeader
                  key={column.label}
                  label={column.label}
                  sortKey={column.sortKey}
                  sort={params.sort}
                  onSort={onSort}
                  filter={
                    column.filterKey && (
                      <IncidentColumnFilter filterKey={column.filterKey} params={params} onChange={onFiltersChange} />
                    )
                  }
                />
              ))}
              <th>
                <span className="visually-hidden">{REVIEW_COLUMN}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={COLUMN_COUNT}>No incidents detected</td>
              </tr>
            )}
            {items.map((incident) => (
              <IncidentRow key={incident.reference} incident={incident} now={now} />
            ))}
          </tbody>
        </Table>
      </div>
      <IncidentsPager
        page={params.page}
        pageSize={INCIDENT_LIST_PAGE_SIZE}
        totalCount={totalCount}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export default IncidentsTable;
